<template>
    <el-dialog :title="(codeToEdit ? $t('common.edit') : $t('common.add')) + ' ' + $t('common.repairCode')"
               :visible.sync="showDialog"
               @close="closeDialog"
               width="500px"
               class="repair-dialog"
               append-to-body
               :close-on-click-modal="false">
        <el-form ref="form"
                 :model="form"
                 :rules="rules"
                 size="small">
            <el-form-item :label="$t('common.repairCode')"
                          prop="repairCode">
                <repair-code-selector ref="code-selector"
                                      :preselect="selectedCode"
                                      :productId="productId"
                                      :multiple="multiple"
                                      @repair-code-selected="codeSelected"
                                      @repair-codes-selected="codesSelected"></repair-code-selector>
            </el-form-item>
            <el-row>
                <div v-for="code in selectedCodes"
                     :key="code.id">
                    <p style="font-size: 10px;">
                        {{code.name}}
                        <i class="el-icon-delete"
                           @click="deleteCode(code.id)"></i>
                    </p>
                </div>
            </el-row>
            <el-row :gutter="20">
                <el-col :span="12">
                    <el-form-item :label="$t('common.regularTime')"
                                  prop="regularTime">
                        <el-input-number @blur="regularChange"
                                         :debounce="0"
                                         :controls="false"
                                         :disabled="selectedCode === undefined && selectedCodes.length === 0"
                                         v-model="form.regularTime"
                                         class="full-width"
                                         :min="0"
                                         :max="10"></el-input-number>
                    </el-form-item>
                    <el-form-item :label="$t('claims.supplementaryRegularTime')"
                                  prop="supplementaryRegularTime"
                                  v-if="!multiple">
                        <el-input-number :controls="false"
                                         :debounce="0"
                                         v-model="form.supplementaryRegularTime"
                                         class="full-width"
                                         :disabled="true"></el-input-number>
                    </el-form-item>
                </el-col>
                <el-col :span="12">
                    <el-form-item :label="$t('common.overTime')"
                                  prop="overTime">
                        <el-input-number @blur="overTimeChange"
                                         :debounce="0"
                                         :controls="false"
                                         :disabled="selectedCode === undefined && selectedCodes.length === 0"
                                         v-model="form.overTime"
                                         class="full-width"
                                         :min="0"
                                         :max="10"></el-input-number>
                    </el-form-item>
                    <el-form-item :label="$t('common.supplementaryOverTime')"
                                  prop="supplementaryOverTime"
                                  v-if="!multiple">
                        <el-input-number :controls="false"
                                         :debounce="0"
                                         v-model="form.supplementaryOverTime"
                                         class="full-width"
                                         :disabled="true"></el-input-number>
                    </el-form-item>
                </el-col>
            </el-row>
            <el-form-item :label="$t('claims.supplementaryComment')"
                          prop="supplementaryComment">
                <el-input :disabled="!commentEnabled"
                          v-model="form.supplementaryComment"
                          type="textarea"
                          :rows="3"></el-input>
            </el-form-item>
        </el-form>
        <span slot="footer">
            <el-button type="primary"
                       @click="addCode">{{codeToEdit ? $t('common.save') : $t('common.add') }}</el-button>
            <el-button @click="closeDialog">{{$t('common.cancel')}}</el-button>
        </span>
    </el-dialog>
</template>

<script>
    import Vue from "vue";
    import RepairCodeSelector from "./RepairCodeSelector";

    export default {
        name: "add-repair-code-dialog",
        components: {
            RepairCodeSelector
        },
        props: {
            productId: {
                required: true
            },
            codeToEdit: {
                default: undefined
            },
            visible: {
                type: Boolean,
                default: false
            },
            multiple: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                form: {
                    regularTime: 0,
                    overTime: 0,
                    supplementaryRegularTime: 0,
                    supplementaryOverTime: 0,
                    supplementaryComment: ""
                },
                rules: {
                    regularTime: [
                        {
                            type: "number",
                            required: true,
                            message: this.$t('common.pleaseRegularTime'),
                            trigger: "blur"
                        }
                    ],
                    overTime: [
                        {
                            type: "number",
                            required: true,
                            message: this.$t('common.pleaseOvertime'),
                            trigger: "blur"
                        }
                    ],
                    supplementaryComment: [
                        {
                            validator: (rule, value, callback) => {
                                if (
                                    this.commentEnabled &&
                                    this.form.supplementaryComment.trim() === ""
                                ) {
                                    callback(new Error(this.$t('common.supplementaryCommentRequired')));
                                } else {
                                    callback();
                                }
                            },
                            trigger: "blur"
                        }
                    ]
                },
                selectedCode: undefined,
                selectedCodes: [],
                showDialog: this.visible
            };
        },
        methods: {
            closeDialog() {
                this.selectedCode = undefined;
                this.selectedCodes = [];
                this.$refs["form"].resetFields();
                this.$refs["code-selector"].reset();
                this.showDialog = false;
                this.$emit("dialogClosed");
            },
            codeSelected(code) {
                this.selectedCodes = [];
                if (code) {
                    this.selectedCode = code;
                } else {
                    this.selectedCode = undefined;
                }
            },
            codesSelected(code) {
                if (this.codeToEdit) {
                    return;
                }
                this.selectedCode = undefined;
                var index = this.selectedCodes.indexOf(code);
                if (code) {
                    if (index === -1) {
                        this.selectedCodes.push(code);
                    }
                }
            },
            addCode() {
                this.$refs["form"].validate(valid => {
                    if (valid) {
                        var codesToAdd = [];
                        if (this.multiple) {
                            var regularPerCode =
                                Math.round(
                                    (this.form.regularTime / this.selectedCodes.length) * 100
                                ) / 100;
                            var overtimePerCode =
                                Math.round(
                                    (this.form.overTime / this.selectedCodes.length) * 100
                                ) / 100;
                            for (var i = 0; i < this.selectedCodes.length; i++) {
                                var code = this.selectedCodes[i];
                                var codeToAdd = {
                                    code: code
                                };
                                if (regularPerCode > code.maxTime) {
                                    codeToAdd.regularTime = code.maxTime;
                                    codeToAdd.supplementaryRegularTime =
                                        Math.round((regularPerCode - code.maxTime) * 100) / 100;
                                    codeToAdd.supplementaryComment = this.form.supplementaryComment;
                                } else {
                                    codeToAdd.regularTime = regularPerCode;
                                    codeToAdd.supplementaryRegularTime = 0;
                                }

                                if (overtimePerCode > code.maxTime) {
                                    codeToAdd.overTime = code.maxTime;
                                    codeToAdd.supplementaryOverTime =
                                        Math.round((overtimePerCode - code.maxTime) * 100) / 100;
                                    codeToAdd.supplementaryComment = this.form.supplementaryComment;
                                } else {
                                    codeToAdd.overTime = overtimePerCode;
                                    codeToAdd.supplementaryOverTime = 0;
                                }

                                codesToAdd.push(codeToAdd);
                            }
                        } else {
                            codesToAdd.push({
                                code: this.selectedCode,
                                ...this.form
                            });
                        }
                        var codeToEdit = this.codeToEdit;
                        this.$emit("codes-selected", { codesToAdd, codeToEdit });
                        this.closeDialog();
                    }
                });
            },
            regularChange(evt) {
                if (this.multiple) {
                    return;
                }
                var val = +evt.currentTarget.value;
                if (val > this.selectedCode.maxTime) {
                    var timeOverMaximum = val - this.selectedCode.maxTime;
                    Vue.nextTick(() => {
                        this.form.regularTime = this.selectedCode.maxTime;
                    });
                    this.form.supplementaryRegularTime = timeOverMaximum;
                    this.form.supplementaryOverTime =
                        this.form.overTime + this.form.supplementaryOverTime;
                    this.form.overTime = 0;
                } else {
                    this.form.supplementaryRegularTime = 0;
                    this.form.supplementaryComment = "";

                    if (this.form.overTime !== 0) {
                        this.form.overTime = this.selectedCode.maxTime - val;
                        this.overTimeChange(this.form.overTime);
                    }
                }
                this.$forceUpdate();
            },
            overTimeChange(evt) {
                if (this.multiple) {
                    return;
                }
                var val = parseFloat(evt.target.value);
                if (val + this.form.regularTime > this.selectedCode.maxTime) {
                    Vue.nextTick(() => {
                        this.form.overTime =
                            this.selectedCode.maxTime - this.form.regularTime;
                    });
                    var timeOverMaximum =
                        val + this.form.regularTime - this.selectedCode.maxTime;
                    this.form.supplementaryOverTime = timeOverMaximum;
                } else {
                    this.form.supplementaryOverTime = 0;
                }
            },
            deleteCode(id) {
                this.selectedCodes = this.selectedCodes.filter(item => item.id !== id);
            }
        },
        computed: {
            commentEnabled: function () {
                return (
                    !this.multiple &&
                    this.selectedCode !== undefined &&
                    (this.form.supplementaryRegularTime > 0 ||
                        this.form.supplementaryOverTime > 0)
                );
            }
        },
        watch: {
            visible: function (val) {
                this.showDialog = val;
            },
            codeToEdit: function (val) {
                if (!val) return;
                this.selectedCode = val.code;
                this.form.regularTime = val.regularTime;
                this.form.overTime = val.overTime;
                this.form.supplementaryRegularTime = val.supplementaryRegularTime;
                this.form.supplementaryOverTime = val.supplementaryOverTime;
                this.form.supplementaryComment = val.supplementaryComment;
            }
        }
    };
</script>

<style scoped>
    .full-width {
        width: 100%;
    }

    .repair-dialog > > > .el-dialog__body {
        padding: 0 15px;
    }
</style>


// WEBPACK FOOTER //
// ClientApp/app/claims/components/AddRepairCodeDialog.vue